import * as AuthSession from 'expo-auth-session';
import * as Google from 'expo-auth-session/providers/google';
import * as WebBrowser from 'expo-web-browser';
import { useEffect, useState } from 'react';

// Required once per app load so a web redirect can hand control back to the
// page that started the sign-in (see expo-auth-session docs).
const completion = WebBrowser.maybeCompleteAuthSession();

// Google's sign-in pages send a Cross-Origin-Opener-Policy that severs the
// popup's `window.opener` once it has been to accounts.google.com. The library
// then posts its result to the popup itself, so the page that started the
// sign-in never hears back and the popup is left rendering the whole site.
// The library has already stored the redirect URL in localStorage by this
// point (that is what the opener's `storage` listener below picks up), so all
// that is left for the popup is to close itself.
if (
    typeof window !== 'undefined'
    && completion.type === 'success'
    && /[#&?](id_token|error)=/.test(window.location.href)
) {
    setTimeout(() => window.close(), 300);
}

const ORIGIN_URL_KEY_PREFIX = 'ExpoWebBrowser_OriginUrl_';

// Real Google Sign-In. Client ids come from the Google Cloud console (OAuth
// consent screen → credentials) — set per-platform via EXPO_PUBLIC_GOOGLE_*_
// CLIENT_ID, per .env.example. Requesting an id_token (not just an access
// token) is what lets the server verify identity without an extra round trip.
function useGoogleIdToken() {
    const [request, response, promptAsync] = Google.useIdTokenAuthRequest({
        webClientId: process.env.EXPO_PUBLIC_GOOGLE_CLIENT_ID,
        iosClientId: process.env.EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID,
        androidClientId: process.env.EXPO_PUBLIC_GOOGLE_ANDROID_CLIENT_ID,
        redirectUri: AuthSession.makeRedirectUri()
    });
    const [popupIdToken, setPopupIdToken] = useState<string | null>(null);

    // Web: the popup writes its final URL to localStorage, which fires `storage`
    // here even when the popup lost its reference to us. Only accept a result
    // whose `state` matches the request this tab started.
    useEffect(() => {
        if (typeof window === 'undefined' || !request) return;

        const onStorage = (event: StorageEvent) => {
            if (!event.key?.startsWith(ORIGIN_URL_KEY_PREFIX) || !event.newValue) return;
            try {
                const url = new URL(event.newValue);
                const params = new URLSearchParams(url.hash.replace(/^#/, '') || url.search);
                if (params.get('state') !== request.state) return;
                const idToken = params.get('id_token');
                if (idToken) setPopupIdToken(idToken);
            } catch { /* Not a URL we understand — ignore. */ }
        };

        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [request]);

    const idToken = (response?.type === 'success' ? response.params.id_token : null) ?? popupIdToken;

    return { request, idToken, promptAsync };
}

export { useGoogleIdToken };
